import React, { useEffect, useState } from "react";
import { Search, Crown, Zap, Sparkles, Gift, Eraser, UserX, Loader2, ShieldCheck, AlertCircle, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const TIER_PILL = {
  free: { label: "Free", bg: "!bg-[#F4F2EC]", Icon: Sparkles },
  pro: { label: "Pro", bg: "!bg-[#C2410C] !text-white", Icon: Zap },
  business: { label: "Business", bg: "!bg-black !text-white", Icon: Crown },
};

const STATUS_PILL = {
  comped: "!bg-[#4A7C59] !text-white",
  trialing: "!bg-[#FFB100]",
  active: "!bg-[#4A7C59] !text-white",
  past_due: "!bg-[#C53030] !text-white",
  canceled: "!bg-[#F4F2EC]",
  free: "!bg-[#F4F2EC]",
};

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(); } catch { return s; }
}

// =====================================================================
// Comp modal
// =====================================================================
const CompModal = ({ open, onClose, target, onCompleted }) => {
  const [tier, setTier] = useState("pro");
  const [expires, setExpires] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTier("pro");
      // Default: 1 year from today
      const d = new Date(); d.setFullYear(d.getFullYear() + 1);
      setExpires(d.toISOString().slice(0, 10));
      setNote("");
    }
  }, [open]);

  if (!open || !target) return null;

  const submit = async () => {
    if (!expires) { toast.error("Expiry date is required."); return; }
    setBusy(true);
    try {
      await api.post(`/super/shops/${target.shop_id}/comp`, {
        tier,
        expires_at: new Date(`${expires}T23:59:59Z`).toISOString(),
        note: note.trim(),
      });
      toast.success(`Comped ${target.email} as ${tier} until ${expires}`);
      onCompleted();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't comp account");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="comp-modal">
      <div className="brutal-card !bg-white w-full max-w-md p-6 space-y-4" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A]">Grant free access</div>
            <h3 className="font-heading text-2xl font-black mt-1 uppercase">Comp Account</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F4F2EC]" data-testid="comp-modal-close"><X size={18}/></button>
        </div>
        <div className="brutal-card !shadow-none border-2 border-black p-3 bg-[#F4F2EC]">
          <div className="font-mono text-xs text-[#4A4A4A]">User</div>
          <div className="font-bold">{target.name || "—"}</div>
          <div className="font-mono text-xs">{target.email}</div>
        </div>

        <div>
          <label className="font-mono text-xs uppercase tracking-widest block mb-1">Tier</label>
          <div className="grid grid-cols-2 gap-2">
            {["pro","business"].map(t => (
              <button
                key={t}
                type="button"
                onClick={()=>setTier(t)}
                className={`brutal-btn !py-2 ${tier===t ? "" : "brutal-btn-secondary"}`}
                data-testid={`comp-tier-${t}`}
              >{t === "pro" ? <Zap size={14} className="inline mr-1"/> : <Crown size={14} className="inline mr-1"/>}{t.toUpperCase()}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="font-mono text-xs uppercase tracking-widest block mb-1">Expires on (required)</label>
          <input
            type="date"
            value={expires}
            onChange={(e)=>setExpires(e.target.value)}
            min={new Date().toISOString().slice(0,10)}
            className="brutal-input w-full"
            data-testid="comp-expires-input"
            required
          />
          <div className="font-mono text-[10px] text-[#4A4A4A] mt-1">
            Set it far in the future (e.g. 2099-12-31) for lifetime access.
          </div>
        </div>

        <div>
          <label className="font-mono text-xs uppercase tracking-widest block mb-1">Note (optional)</label>
          <input
            type="text"
            value={note}
            onChange={(e)=>setNote(e.target.value)}
            placeholder="e.g. Beta tester, family, etc."
            maxLength={200}
            className="brutal-input w-full"
            data-testid="comp-note-input"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="brutal-btn brutal-btn-secondary flex-1" disabled={busy}>Cancel</button>
          <button onClick={submit} className="brutal-btn flex-1" disabled={busy} data-testid="comp-submit-btn">
            {busy ? <><Loader2 size={14} className="inline mr-1 animate-spin"/> Comping…</> : <><Gift size={14} className="inline mr-1"/> Grant access</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Destructive confirm modal (wipe / delete)
// =====================================================================
const ConfirmDestructiveModal = ({ open, onClose, title, body, target, danger="Type DELETE to confirm", onConfirm, confirmLabel="Delete" }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setText(""); }, [open]);
  if (!open || !target) return null;

  const submit = async () => {
    if (text !== "DELETE") { toast.error('Type DELETE in the box to confirm.'); return; }
    setBusy(true);
    try { await onConfirm(); onClose(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="confirm-destructive-modal">
      <div className="brutal-card !bg-white w-full max-w-md p-6 space-y-4 border-[#C53030]" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-[#C53030] flex items-center gap-1"><AlertCircle size={14}/> Destructive</div>
            <h3 className="font-heading text-2xl font-black mt-1 uppercase">{title}</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F4F2EC]"><X size={18}/></button>
        </div>
        <div className="brutal-card !shadow-none border-2 border-black p-3 bg-[#F4F2EC]">
          <div className="font-mono text-xs text-[#4A4A4A]">{target.email ? "Account" : "Target"}</div>
          <div className="font-bold">{target.name || target.email || target.shop_id}</div>
          {target.email && <div className="font-mono text-xs">{target.email}</div>}
        </div>
        <div className="font-mono text-sm leading-relaxed">{body}</div>
        <div>
          <label className="font-mono text-xs uppercase tracking-widest block mb-1">{danger}</label>
          <input
            type="text"
            value={text}
            onChange={(e)=>setText(e.target.value)}
            className="brutal-input w-full"
            placeholder="DELETE"
            data-testid="confirm-destructive-input"
            autoFocus
          />
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="brutal-btn brutal-btn-secondary flex-1" disabled={busy}>Cancel</button>
          <button
            onClick={submit}
            className="brutal-btn flex-1 !bg-[#C53030] !text-white !border-black"
            disabled={busy || text !== "DELETE"}
            data-testid="confirm-destructive-submit"
          >
            {busy ? <><Loader2 size={14} className="inline mr-1 animate-spin"/> Working…</> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Admin page
// =====================================================================
export default function Admin() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [compTarget, setCompTarget] = useState(null);
  const [wipeTarget, setWipeTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const search = async (term = q) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/super/users`, { params: term ? { q: term } : {} });
      setUsers(data.users || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load users");
    } finally { setLoading(false); }
  };

  useEffect(() => { search(""); /* initial load */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user?.is_super_admin) {
    return (
      <div className="brutal-card p-8 max-w-xl mx-auto text-center space-y-2">
        <div className="font-heading text-2xl font-black uppercase">Forbidden</div>
        <div className="font-mono text-sm text-[#4A4A4A]">This area is reserved for the platform owner.</div>
      </div>
    );
  }

  const uncomp = async (u) => {
    try {
      await api.post(`/super/shops/${u.shop_id}/uncomp`);
      toast.success(`Removed comp for ${u.email}`);
      search();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't uncomp");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A] flex items-center gap-1">
          <ShieldCheck size={12}/> Platform Owner
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black uppercase">Admin</h1>
        <p className="font-mono text-sm text-[#4A4A4A] mt-2 max-w-2xl">
          Grant free access (comped accounts), wipe inventory data, or fully delete a user. Everything here is logged with your email.
        </p>
      </div>

      <div className="brutal-card p-4 flex items-center gap-2">
        <Search size={16} className="text-[#4A4A4A]"/>
        <input
          type="text"
          value={q}
          onChange={(e)=>setQ(e.target.value)}
          onKeyDown={(e)=> e.key === "Enter" && search()}
          placeholder="Search by email, name, or user_id…"
          className="brutal-input flex-1 !border-0 !shadow-none !p-0"
          data-testid="admin-search-input"
        />
        <button onClick={()=>search()} className="brutal-btn !py-2" data-testid="admin-search-btn">
          {loading ? <Loader2 size={14} className="inline animate-spin"/> : "Search"}
        </button>
      </div>

      <div className="brutal-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#111] text-white font-mono uppercase text-xs">
            <tr>
              <th className="text-left p-3">User</th>
              <th className="text-left p-3">Tier</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Expires</th>
              <th className="text-left p-3">Note</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center font-mono text-xs text-[#4A4A4A]">
                {loading ? "Loading…" : "No users match."}
              </td></tr>
            )}
            {users.map((u) => {
              const tier = TIER_PILL[u.tier] || TIER_PILL.free;
              const TierIcon = tier.Icon;
              const statusCls = STATUS_PILL[u.status] || STATUS_PILL.free;
              const isComped = u.status === "comped";
              const isSelf = u.user_id === user?.user_id;
              const expires = isComped ? u.current_period_end : (u.status === "trialing" ? u.trial_end_at : null);
              return (
                <tr key={u.user_id} className="border-t-2 border-black hover:bg-[#F4F2EC]" data-testid={`admin-row-${u.user_id}`}>
                  <td className="p-3">
                    <div className="font-bold">{u.name || "—"}</div>
                    <div className="font-mono text-xs text-[#4A4A4A]">{u.email}</div>
                    <div className="font-mono text-[10px] text-[#4A4A4A]">{u.user_id} · {u.role}</div>
                  </td>
                  <td className="p-3">
                    <span className={`brutal-badge ${tier.bg} !py-0.5 !px-1.5`}>
                      <TierIcon size={11}/> <span className="ml-1">{tier.label}</span>
                    </span>
                  </td>
                  <td className="p-3">
                    <span className={`brutal-badge ${statusCls} !py-0.5 !px-1.5 uppercase`}>{u.status || "free"}</span>
                  </td>
                  <td className="p-3 font-mono text-xs">{fmtDate(expires)}</td>
                  <td className="p-3 font-mono text-xs text-[#4A4A4A] max-w-[200px] truncate">{u.comped_note || "—"}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 justify-end flex-wrap">
                      {isComped ? (
                        <button onClick={()=>uncomp(u)} className="brutal-btn brutal-btn-secondary !py-1 !px-2 text-xs" data-testid={`admin-uncomp-${u.user_id}`}>
                          Uncomp
                        </button>
                      ) : (
                        <button onClick={()=>setCompTarget(u)} className="brutal-btn !py-1 !px-2 text-xs" data-testid={`admin-comp-${u.user_id}`}>
                          <Gift size={12} className="inline mr-1"/> Comp
                        </button>
                      )}
                      <button
                        onClick={()=>setWipeTarget(u)}
                        className="brutal-btn brutal-btn-secondary !py-1 !px-2 text-xs !border-[#C53030] !text-[#C53030]"
                        data-testid={`admin-wipe-${u.user_id}`}
                      >
                        <Eraser size={12} className="inline mr-1"/> Wipe
                      </button>
                      <button
                        onClick={()=>setDeleteTarget(u)}
                        disabled={isSelf}
                        className="brutal-btn !py-1 !px-2 text-xs !bg-[#C53030] !text-white !border-black disabled:opacity-40"
                        title={isSelf ? "Use Billing → Delete account for yourself" : "Fully delete account"}
                        data-testid={`admin-delete-${u.user_id}`}
                      >
                        <UserX size={12} className="inline mr-1"/> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CompModal
        open={!!compTarget}
        target={compTarget}
        onClose={()=>setCompTarget(null)}
        onCompleted={search}
      />
      <ConfirmDestructiveModal
        open={!!wipeTarget}
        target={wipeTarget}
        title="Wipe shop data"
        body={<>This deletes every product, event, sale, and adjustment for this shop. The user account stays and they can sign in. <span className="font-bold">This cannot be undone.</span></>}
        confirmLabel="Wipe inventory"
        onConfirm={async () => {
          const { data } = await api.delete(`/super/shops/${wipeTarget.shop_id}/data`);
          toast.success(`Wiped ${data.deleted.products} products, ${data.deleted.sales} sales, ${data.deleted.events} events`);
          search();
        }}
        onClose={()=>setWipeTarget(null)}
      />
      <ConfirmDestructiveModal
        open={!!deleteTarget}
        target={deleteTarget}
        title="Delete account"
        body={<>This permanently removes the user, their sessions, all inventory data, and (if they're a shop admin) their teammates. <span className="font-bold">This cannot be undone.</span></>}
        confirmLabel="Delete account"
        onConfirm={async () => {
          const { data } = await api.delete(`/super/users/${deleteTarget.user_id}`);
          toast.success(`Deleted ${deleteTarget.email} (${Object.values(data.deleted).reduce((a,b)=>a+b,0)} records)`);
          search();
        }}
        onClose={()=>setDeleteTarget(null)}
      />
    </div>
  );
}
